/**
 * JWT helpers — decode only, never verify.
 *
 * Verification is the API's job; the browser is not the party that validates
 * this token. All the client needs is `exp`, so the in-memory store can mark the
 * token stale at the same moment the server will, and `sub` for display.
 *
 * Shared by the real login flow and the dev-token seed so there is exactly one
 * decoder. Legacy has two that disagree: `isTokenExpired` (apiservice.js:90)
 * does an unguarded `atob` + `JSON.parse`, so a malformed payload throws out of
 * the request interceptor instead of reporting "expired" — the request then
 * fails with a decode error rather than triggering a refresh.
 */

/**
 * Strip what a copy-paste commonly drags along with a token.
 *
 * Legacy does the same on the way out of the cookie —
 * `decodeURIComponent(Cookies.get('jwt_token')).replaceAll('"', '')` — because
 * the value is stored JSON-quoted. A single stray quote makes the whole header
 * invalid in a way that looks exactly like a rejected-but-well-formed token.
 */
export function cleanToken(raw) {
  return decodeURIComponent(String(raw).trim())
    .replace(/^["']|["']$/g, '')
    .replace(/^Bearer\s+/i, '')
    .trim();
}

/** The payload of a JWT, or null when it is not one. Never throws. */
function decodeClaims(rawToken) {
  try {
    const token = cleanToken(rawToken);
    const payload = token.split('.')[1];
    if (!payload) return null;

    // base64url -> base64, then decode. atob rejects the url-safe alphabet.
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return claims && typeof claims === 'object' ? claims : null;
  } catch {
    return null;
  }
}

/**
 * Has the token passed its `exp`? Legacy's isTokenExpired (apiservice.js:90-109):
 * a missing or unreadable token counts as expired, one with no `exp` does not.
 *
 * Read from the token itself, not from readTokenClaims' `expiresInSeconds`:
 * that is clamped to 0 for an expired token, and 0 means "no stated expiry"
 * to tokenStore — so an expired token used to pass as one that never expires.
 */
export function isTokenExpired(rawToken) {
  if (!rawToken) return true;
  const claims = decodeClaims(rawToken);
  if (!claims) return true;
  const exp = Number(claims.exp);
  if (!claims.exp || !Number.isFinite(exp)) return false;
  return exp <= Math.floor(Date.now() / 1000);
}

/**
 * @returns {{ email: string, expiresInSeconds: number } | null} null when the
 *   input is not a readable JWT. Never throws — this runs on app boot.
 */
export function readTokenClaims(rawToken) {
  const claims = decodeClaims(rawToken);
  if (!claims) return null;

  const secondsLeft = Number(claims.exp) - Math.floor(Date.now() / 1000);
  return {
    email: claims.sub ?? '',
    // Clamped at 0, which callers read as "no stated expiry" — so this is NOT
    // the way to ask whether a token has expired; isTokenExpired is.
    expiresInSeconds: Number.isFinite(secondsLeft) ? Math.max(secondsLeft, 0) : 0,
  };
}
